import React, { useEffect, useState } from 'react';
import { Box, Collapse, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const ITEM_DEFAULT = '#c4ccd8';
const ITEM_ACTIVE = '#f97316';
const ITEM_ACTIVE_BG = 'rgba(249, 115, 22, 0.14)';

const SUB_ITEMS = [
  { path: '/xero/reports', label: 'Reports', icon: <AssessmentOutlinedIcon sx={{ fontSize: 18 }} /> },
  { path: '/xero/invoices', label: 'Invoices', icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} /> },
  { path: '/xero/customers', label: 'Customers', icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} /> },
];

type Props = {
  pathname: string;
  navigate: (path: string) => void;
  onNavigate: () => void;
};

export function XeroNavGroup({ pathname, navigate, onNavigate }: Props) {
  const [open, setOpen] = useState(() => pathname.startsWith('/xero'));
  const parentSelected = pathname.startsWith('/xero');

  useEffect(() => {
    if (pathname.startsWith('/xero')) {
      setOpen(true);
    }
  }, [pathname]);

  const subStyles = (path: string) => {
    const selected = pathname === path || (path === '/xero/reports' && pathname === '/xero');
    return {
      pl: 3,
      py: 0.75,
      borderRadius: 1.5,
      mb: 0.25,
      borderLeft: selected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
      bgcolor: selected ? ITEM_ACTIVE_BG : 'transparent',
      color: selected ? ITEM_ACTIVE : ITEM_DEFAULT,
      '&:hover': {
        bgcolor: selected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
      },
    };
  };

  return (
    <Box>
      <ListItemButton
        onClick={() => {
          if (!open) {
            navigate('/xero/reports');
            onNavigate();
          }
          setOpen((v) => !v);
        }}
        sx={{
          borderRadius: 1.5,
          py: 1,
          mb: 0.25,
          pl: 2,
          borderLeft: parentSelected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
          bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'transparent',
          color: parentSelected ? ITEM_ACTIVE : ITEM_DEFAULT,
          '&:hover': {
            bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
          },
        }}
      >
        <ListItemIcon sx={{ color: parentSelected ? ITEM_ACTIVE : 'inherit', minWidth: 40 }}>
          <AccountBalanceOutlinedIcon sx={{ fontSize: 22, color: '#34d399' }} />
        </ListItemIcon>
        <ListItemText
          primary="Xero"
          primaryTypographyProps={{ fontWeight: parentSelected ? 700 : 500, fontSize: '0.9rem' }}
        />
        {open ? <ExpandLess sx={{ color: ITEM_DEFAULT }} /> : <ExpandMore sx={{ color: ITEM_DEFAULT }} />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 1, pr: 0.5, pb: 0.5 }}>
          {SUB_ITEMS.map((item) => (
            <ListItemButton
              key={item.path}
              onClick={() => {
                navigate(item.path);
                onNavigate();
              }}
              sx={subStyles(item.path)}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.82rem' }} />
            </ListItemButton>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
